import { useEffect, useMemo } from 'react';
import { Select, TextField } from '../../components';
import { DOCTOR_ROLE, PressureSensitivityEnum, PreferredSleepingPositionEnum } from '../../resources/enums';
import { useTranslation } from 'react-i18next';
import { debounce } from 'lodash';
import { SleepClimateEnum } from '../../resources/enums/sleep-climate-enum';
import { getAccount } from '../../redux/selectors';
import { useSelector } from '../../redux/store';

export interface IUpdateScanData {
  weight?: number;
  sleepClimate?: SleepClimateEnum;
  pressureSensitivity?: PressureSensitivityEnum;
  preferredSleepingPosition?: PreferredSleepingPositionEnum;
}

interface ScanUpdateFormProps {
  data: IUpdateScanData;
  setData: (data: IUpdateScanData) => void;
  onUpdate: (data: IUpdateScanData) => void;
}

const ScanUpdateForm = ({ data, setData, onUpdate }: ScanUpdateFormProps) => {
  const { t } = useTranslation();

  const account = useSelector(getAccount);

  const debouncedUpdateDataScan = useMemo(
    () =>
      debounce(async (data: IUpdateScanData) => {
        await onUpdate(data);
      }, 500),
    [onUpdate],
  );

  useEffect(() => {
    return () => {
      debouncedUpdateDataScan.cancel();
    };
  }, [debouncedUpdateDataScan]);

  if (account.doctorRole !== DOCTOR_ROLE.MATRATZEN_EXPERT) {
    return null;
  }

  return (
    <div className="space-y-2">
      <TextField
        name="weight"
        label={t('common.weight')}
        value={data.weight}
        type="number"
        endAdornment={<span className="text-17p text-gray-dark">{t('common.kg')}</span>}
        onChange={(e) => {
          const newData = { ...data, weight: Number(e.target.value) };
          setData(newData);
          debouncedUpdateDataScan(newData);
        }}
        onBlur={() => onUpdate(data)}
      />
      <Select
        label={t('common.sleepClimate')}
        options={Object.values(SleepClimateEnum)}
        getOptionLabel={(item) => t(`common.sleepClimateOptions.${item}`)}
        value={data.sleepClimate}
        onChange={(value) => {
          const newData = { ...data, sleepClimate: value };
          setData(newData);
          debouncedUpdateDataScan(newData);
        }}
      />
      <Select
        label={t('common.pressureSensitivity')}
        options={Object.values(PressureSensitivityEnum)}
        getOptionLabel={(item) => t(`common.pressureSensitivityOptions.${item}`)}
        value={data.pressureSensitivity}
        onChange={(value) => {
          const newData = { ...data, pressureSensitivity: value };
          setData(newData);
          debouncedUpdateDataScan(newData);
        }}
      />
      <Select
        label={t('common.preferredSleepingPosition')}
        options={Object.values(PreferredSleepingPositionEnum)}
        getOptionLabel={(item) => t(`common.preferredSleepingPositionOptions.${item}`)}
        value={data.preferredSleepingPosition}
        onChange={(value) => {
          const newData = { ...data, preferredSleepingPosition: value };
          setData(newData);
          debouncedUpdateDataScan(newData);
        }}
      />
    </div>
  );
};

export default ScanUpdateForm;
